<script>
  import { onMount, onDestroy } from "svelte";
  import { lang } from '../lib/stores';
  import { wordsAnnotations, verbsAnnotations } from "../data/annotations";
  import Words from "./Words.svelte";
  import WordsBars from "./WordsBars.svelte";
  import Beeswarm from "./Beeswarm.svelte";
  import Bars from "./Bars.svelte";
  import LinksToSongs from "./LinksToSongs.svelte";

  export let data;
  let innerWidth = window.innerWidth;
  $: ratio = 25;
  $: innerWidth, size();

  let selectedLang;
	const unsubscribe = lang.subscribe((value) => selectedLang = value);

  const size = () => {
    if (window.matchMedia(`screen and (min-width: 768px)`).matches) {
      ratio = 50;
    } else {
      ratio = 100;
    }
  };

	onDestroy(unsubscribe);
  onMount(() => {
    size();
  });

  $: data, size();
</script>

<svelte:window bind:innerWidth />
<section class="chapter-1 chapter-inner">
  <div class="p">
    <p>
      {#if selectedLang === 'it'}
        Secondo i nostri calcoli, nelle canzoni contenute nei suoi tre primi album
        Ghali ha utilizzato:
      {:else}
        According to our calculations, in the songs included in his first three albums Ghali used:
      {/if}
    </p>
  </div>
  <div class="schema">
    <div class="block">
      <h3>
        {new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(
          Number(data?.stats?.totalWords ?? 0),
        )}
      </h3>
      <p>
        {#if selectedLang === 'it'}
          Singole parole, che diventano…
        {:else}
          Single words, which become…
        {/if}
      </p>
    </div>
    <div class="block">
      <h3>
        {new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(
          Number(data?.stats?.wordsWithoutOnomatopeic ?? 0),
        )}
      </h3>
      <p>
        {#if selectedLang === 'it'}
          … se togliamo le cosiddette "stopwords" (congiunzioni, preposizioni,
          verbi ausiliari e comuni), i suoni onomatopeici e le esclamazioni
          monosillabiche e…
        {:else}
          … if we remove the so-called "stopwords" (conjunctions, prepositions, auxiliary and common verbs), onomatopoeic sounds and monosyllabic exclamations and…
        {/if}
      </p>
    </div>
    <div class="block">
      <h3>
        {new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(
          Number(data?.stats?.wordsWithoutForeign ?? 0),
        )}
      </h3>
      <p>
        {#if selectedLang === 'it'}
          … se togliamo le parole di origine straniera (che nel caso di Ghali
          includono parole in inglese, francese, spagnolo e arabo).
        {:else}
          … if we remove the words of foreign origin (which in Ghali's case include words in English, French, Spanish and Arabic).
        {/if}
      </p>
    </div>
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === 'it'}
        Ma sono tante o poche?
      {:else}
        But are they many or few?
      {/if}
    </h3>
    <h2>
      {#if selectedLang === 'it'}
        Facciamo qualche confronto
      {:else}
        Let's make some comparisons
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === 'it'}
        Il <a href="https://www.treccani.it/" target="_blank"
          >Vocabolario Treccani</a>
        contiene circa <em class="data">100.000</em> parole; questo numero non
        rappresenta il totale delle parole italiane, che sono stimate essere oltre
        <em class="data">247.000</em>, ma solo quelle definite all'interno del
        Vocabolario.
      {:else}
        The <a href="https://www.treccani.it/" target="_blank">Treccani Vocabulary</a>
        contains approximately <em class="data">100,000</em> words; this number does not represent the total number of Italian words, which are estimated to be over
        <em class="data">247,000</em>, but only those defined within the Vocabulary.
      {/if}
    </p>
    <p>
      {#if selectedLang === 'it'}
        Sempre <a
          href="https://www.treccani.it/magazine/lingua_italiana/domande_e_risposte/lessico/lessico_267.html"
          target="_blank">Treccani</a
        >
        suddivide questi vocaboli in sottinsiemi: il <em>lessico comune</em> è
        composto da circa <em class="data">47.000</em> parole: di queste,
        <em class="data">6.500</em>
        sono il cosiddetto <em>vocabolario di base</em>, costituito da
        <em>lessico fondamentale</em>
        (circa <em class="data">2.000</em> parole), di <em>alto uso</em> e di
        <em>alta disponibilità</em>.
      {:else}
        <a href="https://www.treccani.it/magazine/lingua_italiana/domande_e_risposte/lessico/lessico_267.html" target="_blank">Treccani</a> also
        divides these words into subsets: the <em>common vocabulary</em> is made up of approximately <em class="data">47,000</em> words: of these,
         <em class="data">6,500</em> are the so-called <em>basic vocabulary</em> , made up of <em>core vocabulary</em> (approximately
         <em class="data">2,000</em> words), <em>highly used</em> and <em>highly available</em>.
      {/if}
    </p>
    <p>
      {#if selectedLang === 'it'}
        Immagina che il rettangolo qua sotto rappresenti le <em class="data"
          >100.000</em
        >
        parole definite dal
        <a href="https://www.treccani.it/" target="_blank">Vocabolario Treccani</a
        >
        (un simbolo corrisponde a <em class="data">{ratio}</em> parole):
      {:else}
        Imagine that the rectangle below represents the <em class="data">100,000</em> 
        words defined by the
         <a href="https://www.treccani.it/" target="_blank">Treccani Vocabulary</a> 
        (one symbol corresponds to <em class="data">{ratio}</em> words):
      {/if}
    </p>
  </div>
  <div class="words-wrapper chart">
    <Words {ratio} {data} />
  </div>
  <div class="p">
    <blockquote>
      <p>
        {#if selectedLang === 'it'}
          Secondo <a
            href="https://www.coffeewriting.it/coffee-corretto/parole-parole-parole-ma-quante-ne-utilizziamo-davvero/#:~:text=Quotidianamente%20un%20italiano%20medio%20fa,composto%20da%20sole%202.000%20parole."
            target="_blank">alcune fonti</a
          >, il vocabolario medio degli italiani è composto da
          <em>2.000 parole</em>.
          <a
            href="https://www.ilgiornale.it/news/l-italiano-medio-usa-solo-800-parole.html"
            target="_blank">Altri</a
          >
          stimano invece che gli italiani, soprattutto giovani, conoscano un numero di
          vocaboli <em>compreso tra 800 e 1.200</em>.
        {:else}
          According to <a href="https://www.coffeewriting.it/coffee-corretto/parole-parole-parole-ma-quante-ne-utilizziamo-davvero/#:~:text=Quotidianamente%20un%20italiano%20medio%20fa,composto%20da%20sole%202.000%20parole." target="_blank">some
          sources</a>, the average vocabulary of Italians is made up of <em>2,000 words</em>. <a href="https://www.ilgiornale.it/news/l-italiano-medio-usa-solo-800-parole.html" target="_blank">Others</a> 
          estimate that Italians, especially young people, know <em>between 800 and 1,200</em> words .
        {/if}
      </p>
    </blockquote>
    <p>
      {#if selectedLang === 'it'}
        Basandosi solo su quanto affermato da <a
          href="https://www.treccani.it/magazine/lingua_italiana/domande_e_risposte/lessico/lessico_267.html"
          target="_blank">Treccani</a
        >
        sembra che il vocabolario di Ghali nei brani analizzati sia
        <em>in linea col lessico fondamentale</em>. C'è però da considerare che il
        lessico fondamentale, usato nel <em class="data">90%</em> dei nostri discorsi di tutti i giorni,
        include anche articoli e preposizioni che noi, invece, abbiamo eliminato
        dal nostro calcolo, oltre a termini e interi versi in arabo, francese e inglese.
      {:else}
        <a href="https://www.treccani.it/magazine/lingua_italiana/domande_e_risposte/lessico/lessico_267.html" target="_blank">Based solely on what Treccani states</a>, it seems that Ghali's vocabulary in the analyzed tracks aligns with the core vocabulary. However, it should be noted that the core vocabulary, which accounts for <em class="data">90%</em> of our everyday speech, also includes articles and prepositions that we, on the other hand, excluded from our calculation, as well as terms and entire verses in Arabic, French, and English.
      {/if}
    </p>
    <p>
      {#if selectedLang === 'it'}
        Proviamo a guardare gli stessi dati in un'altra forma:
      {:else}
        Let's try to look at the same data in another form:
      {/if}
    </p>
  </div>
  <div class="bars-wrapper chart">
    <WordsBars data={data?.stats?.wordsWithoutForeign ?? 0} />
  </div>
  <blockquote>
    <p>
      {#if selectedLang === 'it'}
        La mescolanza di lingue europee degli ex imperi coloniali con dialetti regionali e inserzioni
        alloglotte <em>forma un catalogo linguistico unico nei brani del rapper Ghali</em> (…) La valorizzazione
        cantata della sua identità transculturale (italiana, tunisina, musulmana, di persona di colore)
        potrebbe non solo simboleggiare una nuova via di comunicazione nel sistema culturale italiano,
        ma soprattutto <em>ridefinire l’italianità bianca, cristiana, con radice europea</em>.
      {:else}
        The combination of standard languages and the vernacular characterized in Ghali's songs is integrated
        within the traditional codes of rap music, therefore serving as a space to express the plight of
        minorities. Furthermore, <em>through his lyrics that portray various cultures</em>, from his local Baggio
        to Milan and Italy as well as Tunisia, <em>Ghali affirms his transcultural identity by exceeding
        national categorizations</em>.
      {/if}
    </p>
    <p class="mt"><small>Justine Noyer in <a href="https://hal.science/hal-04347411/file/Justine_Noyer_Le_scelte_linguistiche_nel_rap_italiano_di_oggi_il_caso_di_Ghali.pdf"
      target="_blank">Le scelte linguistiche nel rap italiano di oggi: il caso di
      Ghali</a></small></p>
  </blockquote>
  <div class="subtitle">
    <h3>
      {#if selectedLang === 'it'}
        Non male, vero?
      {:else}
      Not bad, right?
      {/if}
    </h3>
    <h2>
      {#if selectedLang === 'it'}
        Vediamo ora quali parole usa Ghali
      {:else}
        Let's now see what words Ghali uses
      {/if}
    </h2>
  </div>
  <div class="p">
    <blockquote>
      <p>
        Lo faccio per la fam, non per gli orologi<br />
        Da piccolo giocavo al rap, sognavo tanti soldi<br />
        <em>Cosa mi direbbe il Ghali di oggi?</em><br />
        1993, sono un vero OG
      </p>
      <p><small>1993, DNA, 2020</small></p>
    </blockquote>
    <p>
      {#if selectedLang === 'it'}
        Il grafico qua sotto mostra le <em class="data"
          >{new Intl.NumberFormat("it-IT").format(
            data?.chapter1?.beeswarmWords?.data?.length ?? 0,
          )}</em
        >
        parole utilizzate almeno
        <em class="data"
          >{data?.chapter1?.beeswarmWords?.minRecurrence ?? 0} volte</em
        >: a sinistra quelle che ricorrono meno, a destra quelle più usate. A
        differenza dell'altro grafico,
        <em>un pallino corrisponde a una parola</em>. Nel grafico abbiamo
        evidenziato le parole a nostro parere più significative.
      {:else}
        The graph below shows the <em class="data"
        >{new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(
          data?.chapter1?.beeswarmWords?.data?.length ?? 0,
        )}</em> 
        words used at least
        <em class="data">{data?.chapter1?.beeswarmWords?.minRecurrence ?? 0} times</em>: on the left
        are the words that are used less frequently, on the right are the words that are used most frequently.
        Unlike the other graph, <em>a dot corresponds to a word</em> . In the graph we have highlighted the
        words that we believe to be the most significant.
      {/if}
    </p>
  </div>
  <div class="beeswarm-wrapper chart">
    <Beeswarm
      data={data?.chapter1?.beeswarmWords?.data ?? []}
      songs={data?.info?.songs ?? {}}
      albums={data?.info?.albums ?? []}
      annotations={wordsAnnotations}
      subject={selectedLang === 'it' ? "Parole" : "Words"}
      verb={selectedLang === 'it' ? "Usate" : "Used"}
      minRecurrence={data?.chapter1?.beeswarmWords?.minRecurrence ?? 0}
      tooltipSubject={selectedLang === 'it' ? "La parola" : "The Word"}
      tooltipVerb={selectedLang === 'it' ? "stata usata" : "has been used"}
    />
  </div>
  <div class="p">
    <p>
      {#if selectedLang === 'it'}
        Abbiamo visto le parole, vediamo ora come vengono usati i verbi. In questo
        caso, abbiamo raggruppato tutti i <em class="data"
          >{new Intl.NumberFormat("it-IT").format(
            data?.chapter1?.beeswarmVerbs?.data?.length ?? 0,
          )}</em
        >
        verbi utilizzati almeno
        <em class="data"
          >{data?.chapter1?.beeswarmVerbs?.minRecurrence ?? 0} volte</em
        >, riportati all'infinito.
      {:else}
        We have seen the words, now let's see how the verbs are used. In this case, we have grouped all <em class="data"
        >{new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(
          data?.chapter1?.beeswarmVerbs?.data?.length ?? 0,
        )}</em> verbs used at least <em class="data"
          >{data?.chapter1?.beeswarmVerbs?.minRecurrence ?? 0} times</em
        >, conjugated in the infinitive form.
      {/if}
    </p>
  </div>
  <div class="beeswarm-wrapper chart">
    <Beeswarm
      data={data?.chapter1?.beeswarmVerbs?.data ?? []}
      annotations={verbsAnnotations}
      songs={data?.info?.songs ?? {}}
      albums={data?.info?.albums ?? []}
      subject={selectedLang === 'it' ? "Verbi" : "Verbs"}
      verb={selectedLang === 'it' ? "Usati" : "Used"}
      tooltipSubject={selectedLang === 'it' ? "Il verbo" : "The Verb"}
      tooltipVerb={selectedLang === 'it' ? "stato usato" : "has been used"}
      minRecurrence={data?.chapter1?.beeswarmVerbs?.minRecurrence}
    />
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === 'it'}
        E se provassimo a contare i gruppi di parole?
      {:else}
        What if try to count groups of Words?
      {/if}
    </h3>
    <h2>
      {#if selectedLang === 'it'}
        Diamo un'occhiata ai Tri-grammi
      {:else}
        Let's take a look at the tri-grams
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === 'it'}
        Non soddisfatti, abbiamo provato a controllare i <em>tri-grammi</em>,
        ovvero abbiamo contato quante volte le sequenze di tre parole si ripetono.
        I cosiddetti <em>n-grams</em> vengono usati spesso in applicazioni che si
        occupano di analisi del linguaggio, per identificare pattern e stilemi
        tipici dei diversi autori; sono utilizzati in machine learning (AI) nei
        software specializzati nella generazione di testi, in quelli che si
        occupano di classificazione delle informazioni e di analisi semantica. Per
        noi è interessante capire se Ghali utilizza dei pattern nei suoi pezzi, e
        quali siano.<br />
        Per l'analisi dei tri-grammi abbiamo utilizzato tutte le
        <em class="data"
          >{new Intl.NumberFormat("it-IT").format(data?.stats?.totalWords)}</em
        >
        parole, senza alcun filtro, ma mostriamo nel grafico solo quelli che ricorrono
        <em>più di dieci volte</em>.
      {:else}
        Not satisfied, we tried to check the <em>tri-grams</em> , that is, we counted how many
        times the sequences of three words are repeated. The so-called <em>n-grams</em> are often
        used in applications that deal with language analysis, to identify patterns and stylistic
        features typical of different authors; they are used in machine learning (AI) in software
        specialized in text generation, in those that deal with information classification and
        semantic analysis. For us, it is interesting to understand if Ghali uses patterns in his
        pieces, and which ones they are. <br>
        For the analysis of the tri-grams, we used all
        <em class="data"
        >{new Intl.NumberFormat(selectedLang === 'it' ? "it-IT" : "en-US").format(data?.stats?.totalWords)}</em
      > 
        words, without any filter, but in the graph we are only showing those that recur
         <em>more than ten times</em>.
      {/if}
    </p>
    <blockquote>
      <p>
        Sai che sono fatto<br />
        <em>Ah ah ah</em>, bro, fuck tutti quelli che<br />
        Sono fan tuoi<br />
        Affari tuoi, affari tuoi<br />
        Sono fatto<br />
        <em>Ah ah ah</em>, bro, fuck tutti quelli che<br />
        Sono fan tuoi, <em>Ah ah ah ah</em>, boy<br />
        <em>Ah ah ah ah ah</em>, boy
      </p>
      <p><small>Marymango, DNA, 2020</small></p>
    </blockquote>
    <p>
      {#if selectedLang === 'it'}
        Nel grafico ci siamo permessi di evidenziare i tri-grammi che a nostro
        parere identificano lo stile di scrittura di Ghali: la ripetizione di
        suoni o parole. Tra i <em class="data">25</em> tri-grammi più usati, ben
        <em class="data">9</em>
        sono costituiti da ripetizioni della stessa parola.
      {:else}
        In the graph we chose to highlight the tri-grams that in our opinion identify Ghali's writing style: the
        repetition of sounds or words. Among the <em class="data">25</em> most used tri-grams, <em class="data">9</em>
        are made up of repetitions of the same word.
      {/if}
    </p>
  </div>
  <div class="ngrams-wrapper chart">
    <Bars data={data?.chapter1?.ngrams ?? []} presetHighlight={true} />
  </div>
  <div class="p">
    <blockquote>
      <p>
        <em>No, non fare</em> lacrimucce<br />
        No, <em>non fare lacrimucce</em><br />
        <em>No, non fare</em> lacrimucce<br />
        No, <em>non fare lacrimucce</em><br />
        <em>No, non fare</em> lacrimucce<br />
      </p>
      <p><small>Lacrime, Album, 2017</small></p>
    </blockquote>
    <p>
      {#if selectedLang === 'it'}
        Rivediamo ora i tri-grammi, concentrandoci sul numero di canzoni in cui
        compaiono. Da notare che, mentre <em>ah ah ah</em> è l'unico trigramma ad
        apparire in tutti e tre gli album analizzati, forse per un caso, i
        tri-grammi <em>no no no</em> e <em>yeah yeah yeah</em> compaiono
        rispettivamente nei brani <em>Mille pare (Bad Times)</em> e
        <em>Good Times</em> entrambi nell'album del 2020 intitolato <em>DNA</em>.
      {:else}
        Let's now review the trigrams, focusing on the number of songs in which they
        appear. Note that, while <em>ah ah ah</em> is the only trigram to appear
        in all three albums analyzed, perhaps by chance, the trigrams <em>no no no</em>
        and <em>yeah yeah yeah</em> appear respectively in the songs <em>Mille pare
        (Bad Times)</em> and <em>Good Times</em> both in the 2020 album entitled <em>DNA</em>.
      {/if}
    </p>
    <p class="medium-up">
      {#if selectedLang === 'it'}
        Clicca sui tri-grammi o sui titoli delle canzoni per vedere le
        connessioni.
      {:else}
        Click on the trigrams or on the song titles to see the connections.
      {/if}
    </p>
  </div>
  <div class="ngrams-songs-wrapper chart">
    <LinksToSongs data={data?.chapter1?.ngramsToSongs ?? []} />
  </div>
  <blockquote>
    <p>
      <em>Habibi, habibi, habibi, habibi</em><br />
      <em>Habibi, habibi, habibi, habibi</em><br />
      <em>Habibi</em>
    </p>
    <p><small>Habibi, Album, 2017</small></p>
  </blockquote>
  <div class="p">
    <p>
      {#if selectedLang === 'it'}
        Possiamo dirlo, quindi: una delle cifre stilistiche dei primi tre album di
        Ghali consiste nella ripetizione della stessa parola, meglio se si tratta
        di una esclamazione monosillabica: una caratteristica tipica della <em
          >musica trap</em
        >.
      {:else}
      We can therefore assert that one of the defining stylistic elements of Ghali's
      first three albums is the repetition of the same word, preferably a monosyllabic
      exclamation—a distinctive characteristic of <em>trap music</em>.
      {/if}
    </p>
  </div>
</section>

<style>
  .schema {
    display: block;
    margin-bottom: 4rem;
  }

  .block {
    display: block;
    margin-bottom: 2rem;
  }

  .block h3 {
    color: var(--accent-2);
    font-family: var(--data-font-family);
    font-weight: 700;
    letter-spacing: 0.05rem;
    margin-bottom: 0;
  }

  @media screen and (min-width: 768px) {
    .schema {
      display: flex;
      margin: 0 -2rem 6rem -2rem;
      width: auto;
    }

    .block {
      flex: 0 0 33.33%;
      margin-bottom: 0;
      padding: 0 2rem;
    }

    .block h3 {
      font-size: 2rem;
      margin-bottom: 0.5rem;
    }
  }

  @media screen and (min-width: 1024px) {
    .schema {
      margin-bottom: 8rem;
    }
  }
</style>
